import React, { createContext, ReactNode, useEffect, useState } from 'react';
import moment from 'moment';
import axios from 'axios';
import { Dictionary } from 'lodash';

interface INewRegistrationContext {
    currentStep: Step
    registration: IRegistration
    isValidRegistration: boolean
    isSummaryOpen: boolean
    isSaving: boolean
    isSaved: boolean
    registrationCompleted?: ISaveResponse

    changeStep: (newStep: Step) => void
    updateRegistration: (newRegistration: IRegistration) => void
    confirmRegistration: () => Promise<void>
    openSummary: () => void
    closeSummary: () => void
}

export interface IRegistration {
    qrCode: string
    warrantyExtensionMonths: number
    warrantyExpirationDate: moment.Moment
    sku: string
    productBrand: string
    shortDescription: string
    isQrCodeMandatory: boolean
    unlimited: boolean
    dealerId: string
    dealerName: string
    dealerCountryCode: string
    purchaseDate: moment.Moment
    serialNumber: string
    publicUserGuid: string
    receiptId: string
    receiptFilename: string
    purchaseType: string
    receiptDownloadUrl: string
    dealerAddress: string
    username: string
    userCountryCode: string
    userFirstname: string
    userLastname: string
}

export interface ISaveResponse {
    isWarrantyExtended: boolean
    unlimited: boolean
    registrationId: string
    warrantyTitle: Dictionary<string>
    warrantyDescription: Dictionary<string>
    warrantyExpirationDate: moment.Moment
    warrantyLegalMonths: number
    warrantyExtensionMonths: number
    warrantyStatus: string
}

export enum Step {
    Customer = "Customer",
    NewCustomer = "NewCustomer",
    Product = "Product",
    Dealer = "Dealer",
}

export let newRegistrationContext = createContext({} as INewRegistrationContext);

let { Provider } = newRegistrationContext;

let NewRegistrationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    let [currentStep, setCurrentStep] = useState(Step.Customer);
    let [isValidRegistration, setValidRegistration] = useState(false);
    let [isSaving, setSaving] = useState(false);
    let [isSummaryOpen, setSummaryOpen] = useState(false);

    let [isSaved, setSaved] = useState(false);
    let [registrationCompleted, setRegistrationCompleted] = useState<ISaveResponse>();

    let [registration, setRegistration] = useState<IRegistration>({
        dealerCountryCode: '',
        dealerId: '',
        dealerName: '',
        isQrCodeMandatory: false,
        unlimited: false,
        publicUserGuid: '',
        purchaseDate: moment(),
        warrantyExpirationDate: moment(),
        purchaseType: '',
        qrCode: '',
        productBrand: '',
        receiptId: '',
        receiptFilename: '',
        serialNumber: '',
        shortDescription: '',
        sku: '',
        receiptDownloadUrl: '',
        dealerAddress: '',
        warrantyExtensionMonths: 0,
        userCountryCode: '',
        userFirstname: '',
        userLastname: '',
        username: ''
    });


    useEffect(() => {
        checkValidity();
    }, [registration])


    let changeStep = (newStep: Step) => {
        setCurrentStep(newStep);
    }

    let updateRegistration = (newRegistration: IRegistration) => {
        setRegistration(newRegistration);
    }

    let checkValidity = () => {
        let errors = [];
        if (!registration.sku || registration.sku === '')
            errors.push('Invalid SKU');

        if (registration.isQrCodeMandatory && registration.qrCode === '')
            errors.push('Invalid QrCode');

        if (registration.dealerName === '')
            errors.push('Invalid Dealer');

        setValidRegistration(errors.length === 0);
    }

    let openSummary = () => {
        setSummaryOpen(true);
    }

    let closeSummary = () => {
        setSummaryOpen(false);
    }

    let confirmRegistration = async () => {
        if (window.confirm("Do you confirm registration?")) {
            try {
                setSaving(true);
                let res = await axios.post('/api/Product/SaveProduct', registration);
                setRegistrationCompleted(res.data);

            } catch (err) {
                setSaving(false);
            }
            finally {
                setSaved(true);
            }
        }
    }


    return (
        <Provider
            value={{
                currentStep,
                registration,
                isValidRegistration,
                isSaving,
                isSaved,
                isSummaryOpen,
                registrationCompleted, 

                changeStep,
                updateRegistration,
                confirmRegistration,
                openSummary,
                closeSummary
            }}
        >
            {children}
        </Provider>
    )
}

export default NewRegistrationProvider;